import React, {useEffect, useState} from 'react';
import AsyncSelect from 'react-select/async';
import {z} from 'zod';
import {useDebouncedCallback} from 'use-debounce';
import {Option} from '../common/MultiSelect';
import {searchUsers} from '../../../service/authorization';
import {getCssVariable} from '../../../utils/style.utils';

interface IBulkInputMultiselectProps {
    placeholder: string;
    defaultOptions: Option[];
    onChange: (value: Option[]) => void;
}

const emailSchema = z.string().email();

function BulkInputEmailMultiselect({defaultOptions, placeholder, onChange}: IBulkInputMultiselectProps): JSX.Element {
    const [selectedOptions, setSelectedOptions] = useState<Option[]>(defaultOptions);
    const [inputValue, setInputValue] = useState<string>('');
    const [debouncedOptions, setDebouncedOptions] = useState<Option[]>([]);
    const [pendingEmails, setPendingEmails] = useState<string[]>([]);

    const isValidEmail = (email: string): boolean => {
        try {
            emailSchema.parse(email);
            return true;
        } catch (e) {
            return false;
        }
    };

    const fetchUserOptions = async (inputValue: string): Promise<{value: string; label: string}[]> => {
        const emailArray = inputValue.split(',').map((email) => email.trim());
        const response = await searchUsers(emailArray);
        return response ? response.map((user) => ({value: user._id, label: user.email})) : [];
    };

    const debouncedLoadOptions = useDebouncedCallback((inputValue: string, callback: (options: Option[]) => void) => {
        fetchUserOptions(inputValue).then((options) => {
            setDebouncedOptions(options);
            callback(options);
        });
    }, 500);

    const handleInputChange = (input: string): void => {
        const emailArray = input.split(',').map((email) => email.trim().toLowerCase());
        setPendingEmails(emailArray);
        setInputValue(input);

        // This is something that allows options to be shown. Seems like some quirk from use-debounce
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        debouncedLoadOptions(input, () => {});
    };

    useEffect(() => {
        if (pendingEmails.length > 0) {
            const validEmails = pendingEmails.filter((email) => isValidEmail(email));
            const loadedEmails = validEmails.filter(
                (email) =>
                    debouncedOptions.some((option) => option.label.toLowerCase() === email) &&
                    !selectedOptions.some((selected) => selected.label.toLowerCase() === email)
            );
            const newSelectedOptions = [
                ...selectedOptions,
                ...debouncedOptions.filter((option) => loadedEmails.includes(option.label.toLowerCase())),
            ];

            setSelectedOptions(newSelectedOptions);
            onChange(newSelectedOptions);

            const remainingEmails = pendingEmails.filter(
                (email) => !isValidEmail(email) || !debouncedOptions.some((option) => option.label.toLowerCase() === email)
            );

            setInputValue(remainingEmails.join(', '));
            setPendingEmails([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedOptions]);

    return (
        <>
            <AsyncSelect
                placeholder={placeholder}
                isMulti
                defaultOptions={defaultOptions}
                loadOptions={debouncedLoadOptions}
                value={selectedOptions}
                inputValue={inputValue}
                onChange={(selected) => {
                    setSelectedOptions(selected as Option[]);
                    onChange(selected as Option[]);
                }}
                onInputChange={(input) => handleInputChange(input)}
                className="c-sidepanel__input_field"
                styles={{
                    indicatorSeparator: () => ({display: 'none'}),
                    control: (base) => ({
                        ...base,
                        backgroundColor: '#4949491a',
                        border: 'none',
                        fontSize: '0.825rem',
                        borderRadius: '0.5rem',
                        outline: 'none',
                        minHeight: '30px',
                        height: 'fit',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#49494926', // <-- background color on hover
                        },
                        cursor: 'pointer',
                    }),
                    container: (base) => ({
                        ...base,
                        height: 'fit-content',
                    }),
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    option: (base, {data, isFocused, isSelected}) => ({
                        ...base,
                        fontSize: '0.875rem',
                        color: '#334155',
                        padding: '0.375rem',
                        borderRadius: '0.375rem',
                        backgroundColor: isFocused || isSelected ? 'rgb(243, 244, 246)' : 'transparent',
                    }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        padding: '0rem 0.625rem',
                        color: '#494949',
                        fontSize: '1rem',
                    }),
                    indicatorsContainer: (base) => ({
                        ...base,
                        padding: '0rem',
                        margin: '0rem',
                    }),
                    placeholder: (base) => ({
                        ...base,
                        color: '#545454',
                    }),
                    input: (base) => ({...base, padding: '0rem', margin: '0rem'}),
                    menu: (base) => ({
                        ...base,
                        position: 'absolute',
                        zIndex: getCssVariable('--z-index-dropdown'),
                        backgroundColor: '#ffffff',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        transition: 'transition: transform 0.2s ease, opacity 0.2s ease;',
                        border: '1px solid rgb(229 231 235)',
                        paddingInline: '0.25rem',
                    }),
                    multiValue: (base) => ({...base, backgroundColor: '#ffffff'}),
                }}
            />
        </>
    );
}

export default BulkInputEmailMultiselect;
